/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('text-clamp')) {
  class TextClamp extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.text-clamp__content');
      this.toggle = this.querySelector('.text-clamp__toggle');
      if (!this.content || !this.toggle) return;

      this.abortController = new AbortController();
      this.toggle.addEventListener('click', this.handleToggle.bind(this), {
        signal: this.abortController.signal
      });

      const debouncedEvaluate = debounce(this.evaluate.bind(this), 300);
      this.resizeObserver = new ResizeObserver(debouncedEvaluate);
      this.resizeObserver.observe(this.content);

      this.evaluate();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /**
     * Reads the active height limit (mobile/desktop resolved by the media
     * query in text-clamp.css).
     * @returns {number}
     */
    getLimit() {
      return parseFloat(getComputedStyle(this).getPropertyValue('--text-clamp-limit'));
    }

    /**
     * Re-checks whether the content exceeds the active limit and syncs state.
     * Runs on connect and (debounced) on content resize, so it also covers
     * viewport changes across the mobile/desktop breakpoint and late reflows
     * (e.g. web font loading).
     */
    evaluate() {
      const limit = this.getLimit();
      const fits = !limit || this.content.scrollHeight - limit <= 1;
      this.classList.toggle('text-clamp--fits', fits);

      if (this.classList.contains('is-expanded')) {
        if (fits) {
          this.close();
        } else {
          // keep the expanded max-height in sync with the reflowed content
          this.style.setProperty('--text-clamp-full', `${Math.round(this.content.scrollHeight)}px`);
        }
      }

      this.updateFocusableElements();
    }

    /**
     * Handles 'click' events on the toggle element.
     * @param {object} evt - Event object.
     */
    handleToggle(evt) {
      evt.preventDefault();

      if (this.classList.contains('is-expanded')) {
        this.close();
      } else {
        this.open();
      }
    }

    /**
     * Expands the content to its full height.
     */
    open() {
      this.style.setProperty('--text-clamp-full', `${Math.round(this.content.scrollHeight)}px`);
      this.classList.add('is-expanded');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.textContent = this.dataset.showLessText;
      this.updateFocusableElements();
    }

    /**
     * Collapses the content back to the height limit.
     */
    close() {
      this.classList.remove('is-expanded');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.textContent = this.dataset.showMoreText;
      this.updateFocusableElements();
    }

    /**
     * Removes links/buttons inside the clipped region from the tab order —
     * focusing an element inside an overflow:hidden box scrolls it into view
     * internally, which would visually corrupt the collapsed clamp. Mirrors
     * the visibility handling in quick-links.js.
     */
    updateFocusableElements() {
      const clamped = !this.classList.contains('text-clamp--fits') && !this.classList.contains('is-expanded');
      const limit = this.getLimit();
      const contentTop = this.content.getBoundingClientRect().top;

      this.content.querySelectorAll('a[href], button').forEach((el) => {
        const hidden = clamped && el.getBoundingClientRect().bottom - contentTop > limit;
        if (hidden) {
          el.setAttribute('tabindex', '-1');
        } else {
          el.removeAttribute('tabindex');
        }
      });
    }
  }

  customElements.define('text-clamp', TextClamp);
}
